<script lang="ts" setup>
import type { ProductPage } from "@/types";

const { product, uuid: productUuid } = defineProps<{
  product: ProductPage;
  uuid: string;
}>();
const { sections, name } = product;

const { cartItems, addItem, removeItem } = useCart();

const isFavorite = computed(() => {
  if (!productUuid) return false;

  return cartItems.value.includes(productUuid);
});

const toggleFavorite = () => {
  if (!productUuid) return;
  isFavorite.value ? removeItem(productUuid) : addItem(productUuid);
};

const items = computed(() =>
  sections.map((section) => ({
    ...section,
    label: section.heading,
  })),
);

const handleShare = () => {
  const subject = "Here's a Smartstone surface I’d like to share with you";
  const body = `Hi, I found a Smartstone surface I want you to check out. Check out ${name} at ${window.location}.`;

  window.location.href = `mailto:?subject=${subject}&body=${body}`;
};
</script>

<template>
  <Container class="pb-8 pt-5 lg:pb-[75px] bg-[#FAF7F4]">
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-[60px]">
      <div class="flex flex-col gap-6">
        <RichText
          v-editable="product"
          class="font-calson"
          :content="product.description"
        />
        <div
          class="flex flex-col md:flex-row items-start md:items-center gap-8 lg:gap-10"
        >
          <button
            class="flex px-5 py-3 gap-2.5 bg-black text-white lg:py-5 lg:px-8 rounded-full"
            @click="toggleFavorite"
          >
            <span>{{
              isFavorite ? "Remove from favourites" : "Add to favourites"
            }}</span>
            <NuxtImg
              v-if="isFavorite"
              width="24px"
              height="24px"
              src="/icon/favourites.svg"
              alt="Remove from favorites"
              class="w-6 h-6 invert"
            />
            <NuxtImg
              v-if="!isFavorite"
              width="24px"
              height="24px"
              src="/icon/add-to-favourites.svg"
              alt="Add to favourite"
              class="h-6 w-6 invert"
            />
          </button>
          <a
            href="javascript:void(0)"
            class="flex gap-2.5 lg:gap-6"
            @click.prevent="handleShare"
          >
            <span class="font-helvetica-bold">Share</span>
            <NuxtImg
              width="24px"
              height="24px"
              src="/icon/chevron-right-rounded.svg"
              alt="Chevron right"
            />
          </a>
        </div>
      </div>

      <div class="product-information-section rounded-md">
        <UAccordion
          :items="items"
          multiple
          class="rounded-[6px] overflow-hidden px-5 bg-white"
        >
          <template #default="{ item, open, index }">
            <UButton
              class="flex justify-between items-center lg:py-[30px] py-5 md:px-5 px-0 bg-white text-lg lg:text-xl font-helvetica-bold cursor-pointer hover:bg-transparent"
              :class="{
                'border-b border-b-[#999]': !open && items.length - 1 !== index,
              }"
              :ui="{
                rounded: 'rounded-none',
              }"
              color="gray"
              variant="ghost"
            >
              <span>{{ item.label }}</span>
              <template #trailing>
                <UIcon
                  name="i-heroicons-chevron-down-20-solid"
                  class="w-5 h-5 ms-auto transform transition-transform duration-200 shrink-0"
                  :class="[open && 'rotate-180']"
                />
              </template>
            </UButton>
          </template>

          <template #item="{ item, open, index }">
            <div
              class="text-[#1D1F22] md:px-5 pt-0 pb-5 text-base"
              :class="{
                'border-b border-b-[#999]': open && items.length - 1 !== index,
              }"
            >
              <ProductInformationSection
                :blok="item"
                :price="product.price"
                :type="product.type"
                :use="product.use"
                :is-specs-tab="index === 0"
              />
            </div>
          </template>
        </UAccordion>
      </div>
    </div>
  </Container>
</template>
